import {
  createApi,
  createCollectionManager,
  createCustomServiceCall,
  Pagination,
} from '@thinknimble/tn-models'
import { z } from 'zod'
import axiosInstance from '../AxiosClient'
import {
  forgotPasswordShape,
  inviteFundraiserShape,
  loginShape,
  userCreateShape,
  userShape,
  updateStatus,
  homeOfficeShape,
  userFiltersShape,
  userRetriveShape,
} from './models'

const LOGIN_ENDPOINT = '/login/'
const PASSWORD_RESET_EMAIL_ENDPOINT = '/password/reset/'
const PASSWORD_RESET_ENDPOINT = '/password/reset/confirm/'
const RESEND_INVITE_ENDPOINT = '/password/reset/resend-invite/'
const USERS_ENDPOINT = '/users/'
const INVITE_FUNDRAISER = '/users/invite_to_canvassing_org/'
const HOME_OFFICE_ENDPOINT = '/homeoffice/'

const login = createCustomServiceCall(
  {
    inputShape: loginShape,
    outputShape: userShape,
  },
  async ({ client, input, utils }) => {
    const res = await client.post(LOGIN_ENDPOINT, utils.toApi(input))
    return utils.fromApi(res.data)
  },
)

const requestPasswordReset = createCustomServiceCall(
  {
    inputShape: forgotPasswordShape,
  },
  async ({ client, input }) => {
    await client.post(`${PASSWORD_RESET_EMAIL_ENDPOINT}${input.email}/`)
  },
)

const resetPassword = createCustomServiceCall(
  {
    inputShape: { uid: z.string(), token: z.string(), password: z.string() },
    outputShape: userShape,
  },
  async ({ client, input, utils }) => {
    const { uid, token, ...rest } = utils.toApi(input)
    const res = await client.post(`${PASSWORD_RESET_ENDPOINT}${input.uid}/${input.token}/`, rest)
    return utils.fromApi(res.data)
  },
)

const resendInviteLink = createCustomServiceCall(
  {
    inputShape: z.object({ uid: z.string() }),
  },
  async ({ client, input }) => {
    await client.post(`${RESEND_INVITE_ENDPOINT}${input.uid}/`)
  },
)

const inviteFundraiser = createCustomServiceCall(
  {
    inputShape: inviteFundraiserShape,
  },
  async ({ client, input, utils }) => {
    await client.post(INVITE_FUNDRAISER, utils.toApi(input))
  },
)

const customUpdate = createCustomServiceCall(
  {
    inputShape: updateStatus,
  },
  async ({ client, input, utils }) => {
    const { id, ...data } = utils.toApi(input)
    const res = await client.patch(`${USERS_ENDPOINT}${id}/`, data)
    return res.data
  },
)

const retrieveFilterIndexes = createCustomServiceCall(
  {
    inputShape: z
      .object({
        canvassingOrg: z.string().uuid().optional(),
      })
      .optional(),
    outputShape: userShape,
  },
  async ({ client, utils, input }) => {
    const params = {}
    if (input?.canvassingOrg) {
      params.canvassing_org = input.canvassingOrg
    }
    const res = await client.get(`${USERS_ENDPOINT}get_filter_values/`, { params })
    return utils.fromApi(res.data)
  },
)
export const homeOfficeApi = createApi({
  baseUri: HOME_OFFICE_ENDPOINT,
  client: axiosInstance,
  models: {
    entity: homeOfficeShape,
  },
})

export const fetchHomeOfficesPage = async (page = 1, pageSize = 25) => {
  const { data } = await axiosInstance.get(HOME_OFFICE_ENDPOINT, {
    params: { page, page_size: pageSize },
  })
  return data
}

const checkBinAvailability = createCustomServiceCall(
  {
    inputShape: z.object({
      bin: z.string().length(6, 'BIN must be 6 digits'),
    }),
    outputShape: z.object({
      allowed: z.boolean(),
    }),
  },
  async ({ client, input }) => {
    const response = await client.get(`/check_bin/`, {
      params: { bin: input.bin },
    })
    return response.data
  },
)

const checkScreenedPhone = createCustomServiceCall(
  {
    inputShape: z.object({
      phone: z.string().min(1),
    }),
    outputShape: z.object({
      allowed: z.boolean(),
    }),
  },
  async ({ client, input }) => {
    const response = await client.get(`/check_screened_phone/`, {
      params: { phone: input.phone },
    })
    return response.data
  },
)

const customRetrieve = createCustomServiceCall(
  {
    inputShape: userRetriveShape,
    outputShape: userShape,
  },
  async ({ client, utils, input }) => {
    const res = await client.get(`${USERS_ENDPOINT}${input.id}/`)
    return utils.fromApi(res.data)
  },
)

export const UserApi = createApi(
  {
    client: axiosInstance,
    baseUri: USERS_ENDPOINT,
    models: {
      create: userCreateShape,
      entity: userShape,
      extraFilters: userFiltersShape,
    },
  },
  {
    inviteFundraiser,
    login,
    requestPasswordReset,
    resetPassword,
    resendInviteLink,
    customUpdate,
    retrieveFilterIndexes,
    checkBinAvailability,
    checkScreenedPhone,
    customRetrieve,
  },
)

export const fundraiserFunctions = () => {
  const fundraiserFilters = {
    ordering: '-givaroo_id',
    fundraiser: '',
    homeOffice: '',
    userStatus: '',
    canvassingOrg: '',
  }

  const fundraiserCollection = createCollectionManager({
    fetchList: UserApi.list,
    filters: fundraiserFilters,
    pagination: new Pagination(),
  })

  const homeOfficeCollection = createCollectionManager({
    fetchList: homeOfficeApi.list,
  })

  return {
    fundraiserCollection,
    fundraiserFilters,
    homeOfficeCollection,
  }
}
